import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { AbstractControl, FormControl, Validators, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import {
    DateService,
    ErrorInterface,
    InsurancePolicyActionService,
    PolicyProductIdEnum,
    PurchasedProductInterface,
    RelationInterface,
} from 'outshared-lib';
import {
    WucBalloonModule, WucButtonModule,
    WucFormFieldLabelModule,
    WucFormFieldModule, WucFormFieldSubtextModule,
    WucInputAlertModule,
    WucInputDatePickerModule, WucListModule,
} from '@inshared/website-ui-components';
import {
    InsuranceCoverageControlComponent,
} from '@app/my-zone/shared/insurance-coverage/insurance-coverage-control/insurance-coverage-control.component';
import { NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'ins-insurance-coverage-form',
    templateUrl: './insurance-coverage-form.component.html',
    styleUrls: ['./insurance-coverage-form.component.scss'],
    standalone: true,
    imports: [
        WucFormFieldModule,
        WucFormFieldLabelModule,
        WucInputDatePickerModule,
        WucInputAlertModule,
        WucFormFieldSubtextModule,
        WucBalloonModule,
        WucListModule,
        WucButtonModule,
        ReactiveFormsModule,
        NgFor,
        NgIf,
        InsuranceCoverageControlComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceCoverageFormComponent {
    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public relation!: RelationInterface;

    public now: Date;
    public productIdEnum: typeof PolicyProductIdEnum;
    public minimumDate: string;
    public isDisabled: boolean;
    public errors: ErrorInterface[];

    public form: UntypedFormGroup;

    constructor(
        private dateService: DateService,
        private insurancePolicyActionService: InsurancePolicyActionService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.now = new Date();
        this.productIdEnum = PolicyProductIdEnum;
        this.isDisabled = false;
        this.errors = [];

        this.setMinimumDate();
        this.createForm();
    }

    public get startDate(): AbstractControl {
        return this.form.get('startDate');
    }

    public get coverageIds(): AbstractControl {
        return this.form.get('coverageIds');
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        this.errors = [];
        this.isDisabled = true;

        this.insurancePolicyActionService
            .addCoverage$({
                coverageIds: this.coverageIds.value,
                productId: this.purchasedProduct.productId,
                productSequenceNumber: this.purchasedProduct.productSequenceNumber,
                startDate: this.startDate.value,
            })
            .subscribe({
                next: () => this.onAddCoverageSuccess(),
                error: (errors) => {
                    this.onAddCoverageErrors(errors);
                },
            });
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            // CoverageIds are set in insurance-coverage-control
            coverageIds: new FormControl([], [Validators.required]),
            startDate: new FormControl('', [Validators.required]),
        });
    }

    private setMinimumDate(): void {
        this.minimumDate = this.dateService.add(1, 'day');
    }

    private onAddCoverageSuccess(): void {
        this.isDisabled = false;
        this.closePanel();
    }

    private onAddCoverageErrors(errors: ErrorInterface[]): void {
        this.isDisabled = false;
        this.errors = errors;
        this.changeDetectorRef.detectChanges();
    }

    private closePanel(): void {
        const queryParams: InsurancesQueryParamsInterface = {
            panel: SelectedPanelEnum.Close,
            productId: this.purchasedProduct.productId,
            productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            scroll: true,
        };

        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge',
        });
    }
}
